import memoize from 'lodash/memoize'
import moment from 'moment'
import { getOvernight } from '../../component/Slice/Slice'
import { getSBSBrandAttributes, getBrandAttributeInclusion } from '../../component/Itinerary/helpers'
import { cabinClassMap, ancillaryMap } from '../../constants'
import { getMerchandisingText } from '../getMerchandisingText/getMerchandisingText'

const eventHandlerWrapper = (cbs, e) => {
  if (e.key === 'Enter' || e.keyCode === 13 || e.key === ' ' || e.keyCode === 32 || e.type === 'click') {
    // Prevents spacebar scroll to bottom action
    e.preventDefault()
    cbs.forEach((cb) => cb())
  }
}

const isMixedCabin = (segments) =>
  segments.length && segments.some((segment) => segment.cabinClass != segments[0].cabinClass)

export const returnToDepartureTab = (cb, flyGaLogic, action, index) => (e) => {
  const callbacks = [
    () => flyGaLogic.fireGtm('send', 'event', 'MFLYListings', `SBS - Clicked "${action}"`),
    () => cb({ id: index + 1 }),
  ]

  eventHandlerWrapper(callbacks, e)
}

/* istanbul ignore next */
export const getNextSliceClick = (itinerary, cb) => (e) => {
  eventHandlerWrapper([() => cb(itinerary)], e)
}

/* istanbul ignore next */
export const goToDetailsClick = (cb, id, itinerary, gaIndex, upsellToken) => (e) => {
  const callbacks = [() => cb({ id, gaIndex, itinerary, upsellToken })]
  eventHandlerWrapper(callbacks, e)
}

export const getMultipleAirlineText = ({ marketingAirlines, airlines }) => {
  let multipleAirlineText = null
  if (Array.isArray(marketingAirlines) && marketingAirlines.length > 1) {
    const multipleAirlineList = marketingAirlines?.map(
      (operator) => airlines.find((airline) => airline.code === operator.code)?.name
    )
    multipleAirlineList.forEach((airline, index) => {
      if (index === 0) {
        multipleAirlineText = airline
      } else if (index === multipleAirlineList.length - 1) {
        multipleAirlineText = `${multipleAirlineText} & ${airline}`
      } else {
        multipleAirlineText = `${multipleAirlineText}, ${airline}`
      }
    })
  }
  return multipleAirlineText
}

export const SBSItineraryData = ({
  itinerary,
  isDepartureSummary,
  isUserLoggedIn,
  activeTabId,
  departureSummaryData,
  tripType,
  isSBSQL,
  showOriginDate,
}) => {
  const {
    marketingAirlines,
    allFareBrandNames,
    allFareBrandAttributes,
    alternateDatesTexts,
    operatedByAirlines,
    slices,
    isCugEligible,
    upsellFareBrands,
    priceKey,
    isExchangeFlight,
    airlines,
  } = itinerary
  const { isOvernight, departInfo, arrivalInfo, layoverInfo, displayText } = slices[0]
  let { duration, segments } = slices[0]

  const airline = marketingAirlines[0]
  const fullAirlineName = airline.name
  const airlineTestId = `airline-name-${fullAirlineName}`
  airline.name = airline.name && airline.name.replace(/( air ?(lines?|ways?)?)$/i, '')

  const getOnlyData = true
  const overnightDays = isSBSQL ? itinerary.overnightDays : getOvernight(slices[0], getOnlyData)
  const arrival = {
    airportName: arrivalInfo?.airport?.name,
    airport: arrivalInfo?.airport?.code,
    time: arrivalInfo?.time?.displayTime,
    overnightDays,
  }
  const departing = {
    airportName: departInfo?.airport?.name,
    airport: departInfo?.airport?.code,
    time: departInfo?.time?.displayTime,
    dateRange: moment(departInfo?.time?.dateTime).format('ddd, MMM D'),
    showOriginDate,
  }

  const transformedSegments = segments.map((segment) => {
    const { departInfo } = segment
    return {
      ...segment,
      time: departInfo?.time?.displayTime,
      airport: departInfo?.airport?.code,
      dateTime: departInfo?.time?.dateTime,
      formattedDate: departInfo?.time?.displayTime,
    }
  })

  let fareBrandName = allFareBrandNames[0]
  if (isMixedCabin(segments)) {
    fareBrandName = 'MIXED CABIN'
  } else if (!fareBrandName && !isSBSQL) {
    const cabinClass = segments?.[0]?.cabinClass
    const cabinClassName = cabinClassMap[cabinClass]
    if (cabinClassName) {
      fareBrandName = cabinClassName
    }
  }

  // TODO: Update price for RT, currently getFireflyPrice is only for OW
  let price = itinerary?.totalPriceWithDecimal || getFireflyPrice(itinerary?.price)
  if (activeTabId === 1 && isExchangeFlight && price.price >= 0) {
    price.text = '+'
  } else if (activeTabId > 1 && !isDepartureSummary) {
    price = getIncrementalPricing(departureSummaryData?.price?.price, itinerary?.totalPriceWithDecimal?.price)
  }
  const seatsAvailable = itinerary.seatsAvailable < 4 ? itinerary.seatsAvailable : null

  const fareBrandBoxTexts = tripType === 'OW' ? ['per person'] : ['round-trip']

  const showMemberSignIn = !isUserLoggedIn && isCugEligible

  const airlineTextArray = operatedByAirlines.map((operator) => operator.airline)
  const airlineText = airlineTextArray && Array.from(new Set(airlineTextArray)).join(', ')
  let operatedByText = airlineText && `Operated by ${airlineText}`

  // For Firefly - pull operatedByText from fly-ql
  if (priceKey) {
    operatedByText = displayText?.operatedByText
  }

  const multipleAirlineText = getMultipleAirlineText({ marketingAirlines, airlines })

  const ancillaryTypes = [
    { name: 'CARRY_ON', description: 'Carry-on bag' },
    { name: 'SEATS', description: 'Advance seat selection' },
    { name: 'CHECKED_BAG', description: 'Checked Bags' },
  ]
  const fareBrandAttributes = getSBSBrandAttributes(allFareBrandAttributes?.[0] ?? [], marketingAirlines)

  let ancillaries = []
  if (fareBrandAttributes.length && !isSBSQL) {
    ancillaryTypes.forEach(({ name, description }) => {
      const inclusion = getBrandAttributeInclusion(fareBrandAttributes, name)
      if (inclusion === 'INCLUDED') {
        ancillaries.push({ name, description, inclusion })
      }
    })
  } else if (isSBSQL) {
    ancillaries = fareBrandAttributes.map(({ type }) => ancillaryMap[type])
  }

  let stopsText = null
  let durationText = null
  if (isDepartureSummary) {
    stopsText = getStopsText(layoverInfo)
    durationText = duration
    duration = null
  }

  return {
    altDateText: alternateDatesTexts?.dateRange,
    airline,
    activeTabId,
    tripType,
    airlineTestId,
    ancillaries,
    layoverInfo,
    segments: transformedSegments,
    arrival,
    fareBrandName,
    departing,
    isOvernight,
    price,
    operatedByText,
    seatsAvailable,
    fareBrandBoxTexts,
    duration,
    upsellFareBrands,
    stopsText,
    durationText,
    showMemberSignIn,
    key: itinerary.id,
    multipleAirlineText,
  }
}

export const getSBSItineraryData = memoize(
  SBSItineraryData,
  ({ itinerary, isDepartureSummary, activeTabId }) => `${itinerary.id}${isDepartureSummary}${activeTabId}`
)

const getStopsText = (layovers) => {
  switch (layovers && layovers.length) {
    case 0:
      return 'Nonstop'
    case 1:
      return '1 Stop'
    case 2:
      return '2 Stops'
    default:
      return 'Nonstop'
  }
}

const SBSMerchandising = ({ activeTabId, label }) => {
  if (label === 'CHEAPEST_NONSTOP') {
    return true
  }
  return activeTabId === 1 && label === 'EARLY_BIRD'
}

export const getSBSMerchandisingLabelData = ({
  merchandisingList,
  departingFrom,
  flyGaLogic,
  activeTabId,
}) => {
  const labelData = getMerchandisingText({
    merchandisingList,
    departingFrom,
    flyGaLogic,
  })

  const SBSCanUseLabel = labelData.shortText && SBSMerchandising({ activeTabId, label: labelData.label })
  if (SBSCanUseLabel) {
    return labelData
  }
  return null
}

export const getEnabledMerchandisingData = (merchandisingArray) => {
  const merchandisingMap = {
    CHEAPEST_NONSTOP: {
      label: 'CHEAPEST_NONSTOP',
      shortText: 'Cheapest Nonstop',
      icon: 'discount',
      color: 'primary.base',
      enabled: true,
    },
    CHEAPEST: {
      label: 'CHEAPEST',
      shortText: 'Cheapest',
      icon: 'discount',
      color: 'primary.base',
      enabled: true,
    },
    BEST_VALUE: {
      label: 'BEST_VALUE',
      shortText: 'Best Value',
      color: 'primary.base',
      icon: 'dollarCircle',
      enabled: true,
    },
    SHORTEST_DURATION: {
      label: 'SHORTEST_DURATION',
      shortText: 'Shortest Duration',
      icon: 'timer',
      color: 'secondary.base',
      enabled: true,
    },
    SHORTEST_LAYOVER: {
      label: 'SHORTEST_LAYOVER',
      shortText: 'Shortest Layover',
      icon: 'timer',
      color: 'secondary.base',
      enabled: true,
    },
    EARLY_BIRD: {
      label: 'EARLY_BIRD',
      shortText: 'Early Bird Flight',
      icon: 'earlyBird',
      color: 'alert.dark',
      enabled: true,
    },
    NIGHT_OWL: {
      label: 'NIGHT_OWL',
      shortText: 'Night Owl',
      enabled: false,
    },
    SPLIT_TICKET: {
      label: 'SPLIT_TICKET',
      shortText: 'Split Ticket',
      enabled: false,
    },
    EXPERT_TRAVELLER: {
      label: 'EXPERT_TRAVELLER',
      shortText: 'Expert Traveller',
      enabled: false,
    },
  }
  let cheapestMerchandisingCount = 0
  return merchandisingArray
    ?.filter((merchandising) => {
      if (!merchandisingMap[merchandising]?.enabled) {
        return false
      }
      if (['CHEAPEST_NONSTOP', 'CHEAPEST'].includes(merchandising)) {
        cheapestMerchandisingCount = cheapestMerchandisingCount + 1
        return cheapestMerchandisingCount !== 2
      }
      return true
    })
    ?.map((merchandising) => {
      return merchandisingMap[merchandising]
    })
}

export const getCombinedMerchandisings = (itinerary) => {
  const sliceLevelMerchandising = Array.isArray(itinerary?.slices?.[0]?.merchandising)
    ? itinerary?.slices?.[0]?.merchandising
    : []
  const itineraryLevelMerchandising = Array.isArray(itinerary?.merchandising) ? itinerary?.merchandising : []
  return [...sliceLevelMerchandising, ...itineraryLevelMerchandising]
}

export const getIncrementalPricing = (basePrice, higherPrice) => {
  const difference = higherPrice - basePrice

  const text = Math.sign(difference) === -1 ? '-' : '+'
  const [dollar, cents] = difference.toFixed(2).split('.')

  return { dollar: `${Math.abs(dollar)}`, cents, text }
}

const getFireflyPrice = (price) => {
  return price.find((typeOfPrice) => typeOfPrice.type === 'AVG_PRICE')
}
